import React, { useEffect, useRef } from "react";
import { Download, AlertTriangle, CheckCircle2, Info, Loader2 } from "lucide-react";

export default function ExportStatusModal({
  open,
  state,
  progress,
  mensagem,
  registrosProcessados,
  erro,
  downloadError,
  fileName,
  onClose,
  onDownload,
  triggerRef,
}) {
  const closeBtnRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (open) closeBtnRef.current?.focus();
    else triggerRef?.current?.focus();
  }, [open, triggerRef]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const isRunning = state === "running";
  const isSuccess = state === "success";
  const isVazio = state === "vazio";
  const isFailure = state === "failure";

  const titulo = isRunning
    ? "Gerando lista de empresas..."
    : isSuccess
      ? "Lista de empresas pronta"
      : isVazio
        ? "Nenhum resultado encontrado"
        : "Não foi possível gerar a lista de empresas";

  const Icon = isRunning ? Loader2 : isSuccess ? CheckCircle2 : isVazio ? Info : AlertTriangle;
  const badgeClass = isFailure ? "warn" : isRunning ? "" : "ok";

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 440 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-status-titulo"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-header">
          <div className="confirm-title">
            <div className={`confirm-badge ${badgeClass}`}>
              <Icon size={16} className={isRunning ? "spin" : undefined} />
            </div>
            <div>
              <h3 id="export-status-titulo" style={{ margin: 0 }}>{titulo}</h3>
            </div>
          </div>
          <button ref={closeBtnRef} type="button" className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {isRunning && (
          <>
            <div className="mf-progress-bar" style={{ marginTop: "0.75rem" }}>
              <div className="mf-progress-fill" style={{ width: `${progress || 0}%` }} />
            </div>
            <p className="muted small" style={{ marginTop: "0.6rem" }}>
              {mensagem || "Processando sua exportação..."}
              {registrosProcessados ? ` · ${registrosProcessados} registros processados` : ""}
            </p>
            <p className="muted small">
              Você pode fechar esta janela: a exportação continua em segundo plano.
            </p>
          </>
        )}

        {isSuccess && (
          <>
            <p className="muted small" style={{ marginTop: "0.6rem" }}>
              {mensagem || "Seu arquivo foi gerado com sucesso."}
            </p>
            {downloadError && <p className="mf-error">{downloadError}</p>}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
              {fileName ? (
                <button type="button" className="btn-primary" onClick={onDownload}>
                  <Download size={16} /> Baixar arquivo
                </button>
              ) : (
                <button type="button" className="btn-primary" onClick={onClose}>Fechar</button>
              )}
            </div>
          </>
        )}

        {isVazio && (
          <>
            <p className="muted small" style={{ marginTop: "0.6rem" }}>
              {mensagem || "Nenhum registro encontrado para os filtros selecionados."}
            </p>
            {!/crédito/i.test(mensagem || "") && (
              <p className="muted small">Seu crédito não foi consumido.</p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
              <button type="button" className="btn-primary" onClick={onClose}>Entendi</button>
            </div>
          </>
        )}

        {isFailure && (
          <>
            <p style={{ margin: "0.75rem 0 1.25rem", fontSize: "0.9rem", color: "var(--text-muted)", lineHeight: 1.6 }}>
              {erro || "Erro ao gerar a lista de empresas."}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" className="btn-primary" onClick={onClose}>Entendi</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
